import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { registerDirtySource } from '@onvice/portal-shell-api';
import type { UnsavedChangesModalProps } from './UnsavedChangesModal';

export interface UseGuardedCloseOptions {
  open: boolean;
  /** Der Dialog hält ungespeicherte Eingaben */
  dirty: boolean;
  /** Läuft gerade eine Aktion, schließt nichts */
  busy?: boolean;
  onClose: () => void;
  /** Standard true */
  closeOnEscape?: boolean;
  /** Für die Rückfrage, z. B. „Zeiteintrag" */
  label?: string;
}

export interface GuardedClose {
  /** Für Klick daneben, X und „Abbrechen" */
  requestClose: () => void;
  /** Für <UnsavedChangesModal {...confirmProps} /> */
  confirmProps: UnsavedChangesModalProps;
}

// Offene Dialoge in Öffnungs-Reihenfolge — Escape gilt nur dem obersten.
const openStack: string[] = [];

/**
 * Dialog-Schutz (Regel „Dokument-Navigation" R5, Entscheid E6): Klick daneben, X und
 * Escape fragen nach, sobald etwas eingegeben ist. Meldet den Dialog zusätzlich beim
 * Verlassen-Schutz des Portals an, damit auch die Zurück-Taste nachfragt.
 */
export function useGuardedClose({ open, dirty, busy = false, onClose, closeOnEscape = true, label }: UseGuardedCloseOptions): GuardedClose {
  const id = useId();
  const [asking, setAsking] = useState(false);
  const live = useRef({ dirty, busy, asking, onClose });
  live.current = { dirty, busy, asking, onClose };

  const requestClose = useCallback(() => {
    const s = live.current;
    if (s.busy) return;
    if (s.dirty) setAsking(true);
    else s.onClose();
  }, []);

  useEffect(() => {
    if (!open) { setAsking(false); return; }
    openStack.push(id);
    return () => {
      const i = openStack.lastIndexOf(id);
      if (i >= 0) openStack.splice(i, 1);
    };
  }, [open, id]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openStack[openStack.length - 1] !== id) return;
      e.stopPropagation();
      if (live.current.asking) setAsking(false);
      else requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, id, requestClose]);

  useEffect(() => {
    if (!open) return;
    return registerDirtySource({
      id: `dialog:${id}`,
      label: label ?? 'Dialog',
      isDirty: () => live.current.dirty,
      discard: () => live.current.onClose(),
    });
  }, [open, id, label]);

  return {
    requestClose,
    confirmProps: {
      open: open && asking,
      label,
      onStay: () => setAsking(false),
      onDiscard: () => { setAsking(false); live.current.onClose(); },
    },
  };
}
